import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import { LanguageTags } from 'i18n';
import isEqual from 'lodash.isequal';
import { User } from 'services/types';
import { copyFromLocalStorage, persistInLocalStorage } from 'utils/localStorageUtils';
import { initUserStatusChecker } from 'utils/userStatusChecker';
import updateHtmlFontSize from 'utils/updateHtmlFontSize';
import getSalesdeskVersion from 'services/getAppVersion';
import { logError } from 'services/Logger';
import { getConfigAllowAgentsPlaceOnHoldManual } from '../../services/getConfigAllowAgentsPlaceOnHoldManual';
import { getConfigForwardChatOmnichannel } from '../../services/getConfigForwardChatOmnichannel';
import { getConfigRoutingMethod } from '../../services/getConfigRoutingMethod';
import { getDepartmentsOfAgent } from '../../services/getDepartmentsOfAgent';
import { getLivechatUserInfo } from '../../services/getLivechatUserInfo';
import { me } from '../../services/me';
import { RoutingMethods, UserInformation, UserType } from '../../types/user';
import AuthContext from './context';
import { BROWSER_NOTIFICATION, SESSION_STORAGE_KEYS, STORAGE_KEYS } from '../../constants/defaultValues';
import { resetPasswordRoutePrefix, samlRoutes } from '../../constants';
import { ChangeAgentStatusArgs } from './types';

const AuthProvider: React.FC<React.PropsWithChildren> = (props) => {
  const { children } = props;
  const { i18n } = useTranslation();
  const [currentUser, setCurrentUser] = useState<UserType>({} as UserType);
  const [currentUserInfo, setCurrentUserInfo] = useState<UserInformation>({} as UserInformation);
  const [forwardChat, setForwardChat] = useState('');
  const [routingMethod, setRoutingMethod] = useState<RoutingMethods | ''>('');
  const [networkErrorFound, setNetworkErrorFound] = useState(false);
  const [allowsManualOnHold, setAllowsAgentsManualOnHold] = useState(null);
  const [showReloadModal, setShowReloadModal] = useState(false);
  const [latestVersion, setLatestVersion] = useState('');
  const navigate = useNavigate();
  const timeout = 21600000; // 6 hours
  const checkSalesdeskVersionInterval = useRef<NodeJS.Timeout | null>(null);

  const currentUserId = useMemo(() => currentUserInfo._id, [currentUserInfo._id]);
  const sessionStorageUserId = sessionStorage.getItem(SESSION_STORAGE_KEYS.USER_ID);

  const { fontSize } = currentUserInfo?.settings?.preferences || {};

  const updateCurrentUserInfo = useCallback((data: Partial<UserInformation>) => {
    setCurrentUserInfo((oldState) => {
      const newState = { ...oldState, ...data };
      if (isEqual(oldState, newState)) {
        return oldState;
      }
      return newState;
    });
  }, []);

  const getUserInfo = useCallback(async () => {
    const id = currentUser?.userId || currentUserId || sessionStorage.getItem(SESSION_STORAGE_KEYS.USER_ID);

    try {
      if (id) {
        const { data: user } = await me();
        const {
          data: { departments = [] },
        } = await getDepartmentsOfAgent(id);
        const { settings } = user;
        const { preferences } = settings;
        updateCurrentUserInfo({
          departments,
          ...user,
        });

        sessionStorage.setItem(SESSION_STORAGE_KEYS.USERNAME, user.username);
        sessionStorage.setItem(SESSION_STORAGE_KEYS.ROLES, JSON.stringify(user.roles));
        const language = localStorage.getItem(STORAGE_KEYS.LANGUAGE) || user.language || LanguageTags.DEFAULT;
        localStorage.setItem(STORAGE_KEYS.LANGUAGE, language);
        i18n.changeLanguage(language);
        localStorage.setItem(STORAGE_KEYS.TIMEOUT, preferences.idleTimeLimit);

        localStorage.setItem(BROWSER_NOTIFICATION.SOUND_VOLUME, user.settings.preferences.notificationsSoundVolume);
        localStorage.setItem(
          BROWSER_NOTIFICATION.NEW_ROOM_NOTIFICATION_SOUND,
          user.settings.preferences.newRoomNotification
        );
        localStorage.setItem(
          BROWSER_NOTIFICATION.NEW_MESSAGE_NOTIFICATION_SOUND,
          user.settings.preferences.newMessageNotification
        );
      } else {
        sessionStorage.clear();
        const { pathname } = window.location;
        if (
          !Object.values(samlRoutes).some((route) => pathname.endsWith(route)) &&
          !pathname.includes(resetPasswordRoutePrefix)
        ) {
          navigate('/login');
        }
      }
    } catch (e) {
      console.log('error', e);
    }
  }, [currentUser?.userId, currentUserId, updateCurrentUserInfo, navigate]);

  const handleCurrentUser = useCallback(
    (data: UserType) => {
      const { authToken } = data;
      const { userId } = data;
      const roles = data.me?.roles || [];
      const language = data.me?.language || LanguageTags.DEFAULT;
      const username = data.me?.username || '';
      sessionStorage.setItem(SESSION_STORAGE_KEYS.USERNAME, username);
      sessionStorage.setItem(SESSION_STORAGE_KEYS.AUTH_TOKEN, authToken!);
      persistInLocalStorage(SESSION_STORAGE_KEYS.AUTH_TOKEN);
      sessionStorage.setItem(SESSION_STORAGE_KEYS.USER_ID, userId);
      persistInLocalStorage(SESSION_STORAGE_KEYS.USER_ID);
      sessionStorage.setItem(SESSION_STORAGE_KEYS.ROLES, JSON.stringify(roles));
      localStorage.setItem(STORAGE_KEYS.LANGUAGE, language);
      i18n.changeLanguage(language);
      Reflect.deleteProperty(data, SESSION_STORAGE_KEYS.AUTH_TOKEN);
      setCurrentUser(data);
    },
    [i18n]
  );

  const handleCurrentUserInformation = useCallback(
    (data: UserInformation) => {
      updateCurrentUserInfo(data);
      getUserInfo();
    },
    [updateCurrentUserInfo, getUserInfo]
  );

  const changeAgentStatus = useCallback(
    ({ status = currentUserInfo.status, statusLivechat = currentUserInfo.statusLivechat }: ChangeAgentStatusArgs) => {
      updateCurrentUserInfo({
        status,
        statusLivechat,
      });
    },
    [updateCurrentUserInfo, currentUserInfo.status, currentUserInfo.statusLivechat]
  );

  const getLivechatInfo = useCallback(async () => {
    const id = sessionStorage.getItem(SESSION_STORAGE_KEYS.USER_ID) || currentUserId;
    if (id) {
      try {
        const { data } = await getLivechatUserInfo(id);
        setNetworkErrorFound(false);
        updateCurrentUserInfo({
          status: data.user.status,
          statusLivechat: data.user.statusLivechat,
          livechat: data.user.livechat,
        });
      } catch (error) {
        setNetworkErrorFound(true);
      }
    }
  }, [currentUserId, updateCurrentUserInfo]);

  const getCurrentAppVersion = useCallback(async () => {
    try {
      const { data } = await getSalesdeskVersion();
      const { VERSION: lastVersion } = data;
      setLatestVersion(lastVersion);

      const storeVersion = sessionStorage.getItem(SESSION_STORAGE_KEYS.APP_VERSION) || '';

      if (storeVersion === '') {
        sessionStorage.setItem(SESSION_STORAGE_KEYS.APP_VERSION, lastVersion);
      }
    } catch (error) {
      logError('unable to get latest salesdesk version', error);
    }
  }, []);

  const setUserLivechat = useCallback(
    (user: User) => {
      updateCurrentUserInfo({
        status: user.status,
        statusLivechat: user.statusLivechat,
        livechat: user.livechat,
      });
    },
    [updateCurrentUserInfo]
  );

  const loadCurrentUser = useCallback(() => {
    const authToken = sessionStorage.getItem(SESSION_STORAGE_KEYS.AUTH_TOKEN)?.toString();
    const userId = sessionStorage.getItem(SESSION_STORAGE_KEYS.USER_ID)?.toString();
    if (authToken && userId) {
      setCurrentUser({ authToken, userId });
    }
  }, []);

  const getUserRoles = useCallback(() => {
    const data = sessionStorage.getItem(SESSION_STORAGE_KEYS.ROLES);
    if (data) {
      return JSON.parse(data);
    }
    return [];
  }, []);

  const isAuthenticated = useCallback(() => {
    const authToken = sessionStorage.getItem(SESSION_STORAGE_KEYS.AUTH_TOKEN);
    const userId = sessionStorage.getItem(SESSION_STORAGE_KEYS.USER_ID);

    if (authToken && userId) {
      return true;
    }

    if (!authToken || !!userId) {
      copyFromLocalStorage(SESSION_STORAGE_KEYS.AUTH_TOKEN, { override: false });
      copyFromLocalStorage(SESSION_STORAGE_KEYS.USER_ID, { override: false });
    }

    const backFilledAuthToken = sessionStorage.getItem(SESSION_STORAGE_KEYS.AUTH_TOKEN);
    const backFilledUserId = sessionStorage.getItem(SESSION_STORAGE_KEYS.USER_ID);
    if (backFilledAuthToken && backFilledUserId) {
      getUserInfo();
      return true;
    }

    return false;
  }, [getUserInfo]);

  const getCurrentUserId = useCallback(() => {
    const userId = sessionStorage.getItem(SESSION_STORAGE_KEYS.USER_ID)?.toString();
    return currentUser.userId || userId;
  }, [currentUser.userId]);

  const getOmnichannelConfig = useCallback(async () => {
    const { data } = await getConfigForwardChatOmnichannel();

    setForwardChat(data.value);
  }, [setForwardChat]);

  const getRoutingMethod = useCallback(async () => {
    const { data } = await getConfigRoutingMethod();

    setRoutingMethod(data.value);
  }, [setRoutingMethod]);

  const getAllowAgentsPlaceOnHoldManual = useCallback(async () => {
    const { data } = await getConfigAllowAgentsPlaceOnHoldManual();

    setAllowsAgentsManualOnHold(data.value);
  }, [setAllowsAgentsManualOnHold]);

  const updateAvatarURL = useCallback(
    (url: string) => {
      updateCurrentUserInfo({
        avatarUrl: url,
      });
    },
    [updateCurrentUserInfo]
  );

  const updateDataDogUser = useCallback(() => {
    const { _id: id, name, email } = currentUserInfo;
    datadogRum.setUser({
      id,
      name,
      email,
    });
  }, [currentUserInfo]);

  useEffect(() => {
    let cleaner: () => void;
    const cleanChecker = async () => {
      const checker = await initUserStatusChecker({ setter: setUserLivechat });
      return checker;
    };
    cleanChecker().then((cleanup) => {
      cleaner = cleanup;
    });

    return () => {
      cleaner?.();
    };
  }, [sessionStorageUserId, setUserLivechat]);

  useEffect(() => {
    if (!currentUserId) {
      getUserInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  useEffect(() => {
    if (currentUserId) {
      getLivechatInfo();
      loadCurrentUser();
      getOmnichannelConfig();
      getAllowAgentsPlaceOnHoldManual();
      getRoutingMethod();
      updateDataDogUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  useEffect(() => {
    if (fontSize) {
      updateHtmlFontSize(fontSize);
      localStorage.setItem(STORAGE_KEYS.FONTSIZE, fontSize);
    }
  }, [fontSize]);

  useEffect(() => {
    sessionStorage.setItem(SESSION_STORAGE_KEYS.APP_VERSION, '');
    getCurrentAppVersion();

    checkSalesdeskVersionInterval.current = setInterval(() => {
      getCurrentAppVersion();
    }, timeout);

    return () => {
      if (checkSalesdeskVersionInterval.current) {
        clearInterval(checkSalesdeskVersionInterval.current);
      }
    };
  }, []);

  useEffect(() => {
    const storeVersion = sessionStorage.getItem(SESSION_STORAGE_KEYS.APP_VERSION) || '';
    if (storeVersion !== '' && latestVersion !== '' && storeVersion !== latestVersion) {
      setShowReloadModal(true);
    }
  }, [latestVersion]);

  const memoizedValues = useMemo(
    () => ({
      currentUser,
      handleCurrentUser,
      isAuthenticated,
      currentUserInfo,
      handleCurrentUserInformation,
      getCurrentUserId,
      changeAgentStatus,
      getUserRoles,
      getLivechatInfo,
      allowsManualOnHold,
      getUserInfo,
      forwardChat,
      updateAvatarURL,
      routingMethod,
      networkErrorFound,
      setNetworkErrorFound,
      updateDataDogUser,
      updateCurrentUserInfo,
      showReloadModal,
      latestVersion,
      setShowReloadModal,
    }),
    [
      currentUser,
      handleCurrentUser,
      isAuthenticated,
      currentUserInfo,
      handleCurrentUserInformation,
      getCurrentUserId,
      changeAgentStatus,
      getUserRoles,
      getLivechatInfo,
      allowsManualOnHold,
      getUserInfo,
      forwardChat,
      updateAvatarURL,
      routingMethod,
      networkErrorFound,
      setNetworkErrorFound,
      updateDataDogUser,
      updateCurrentUserInfo,
      showReloadModal,
      latestVersion,
      setShowReloadModal,
    ]
  );

  return <AuthContext.Provider value={memoizedValues}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
